/**
 * Authorization. One table, one file, no exceptions elsewhere.
 *
 * ## How to read this
 *
 * `RULES` is `Record<Role, Record<ResourceKind, Rule>>`. Because both halves
 * are exhaustive `Record` types, adding a resource kind or a role fails the
 * typecheck until every combination has been decided. That is the point: a new
 * resource cannot reach production merely because somebody forgot to guard it.
 * The failure mode of a permission system is silence, so this one is loud.
 *
 * ## Two halves of a decision
 *
 * `actions` answers "may this role ever do this?" and `scope` answers "to which
 * rows?". Both must pass. `scope` is returned to the caller as data rather than
 * applied here, because the API turns it into a single SQL predicate in one
 * helper (`applyScope`) - so a route cannot forget to filter, and the filter is
 * tested once instead of per route.
 *
 * ## The three denials this file exists to guarantee
 *
 *  1. A contractor never sees another contractor's hours, rate or earnings.
 *  2. A contractor never sees any client-facing money - billing rates,
 *     invoices, spend or ROI.
 *  3. A client never sees another client, and never sees any person-level data.
 *
 * Everything else is convenience. These three are the product.
 *
 * This module is imported by the web app as well, but ONLY to decide which
 * controls to render. The web is not a security boundary; the API is. Every
 * route calls `authorize` server-side regardless of what the UI did.
 */
import type { Role } from './enums.js';

/* -------------------------------------------------------------------------- */
/* Vocabulary                                                                 */
/* -------------------------------------------------------------------------- */

export interface Actor {
  id: string;
  role: Role;
  /** Set for `client` users, null for owner and contractor. */
  clientId: string | null;
}

export const ACTIONS = ['list', 'read', 'create', 'update', 'delete', 'move', 'assign', 'approve'] as const;
export type Action = (typeof ACTIONS)[number];

export const RESOURCE_KINDS = [
  'client',
  'application',
  'project',
  'feature',
  'task',
  'user',
  'timeEntry',
  'payRate',
  'billingRate',
  'invoice',
  'benefit',
  'spend',
  'capacity',
] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

/**
 * Which rows a role may touch.
 *
 * - `all`    every row
 * - `client` only rows belonging to the actor's own client
 * - `self`   only rows about the actor themselves
 * - `none`   no rows; the action is refused outright
 */
export type ScopeKind = 'all' | 'client' | 'self' | 'none';

export type Scope =
  | { kind: 'all' }
  | { kind: 'client'; clientId: string }
  | { kind: 'self'; userId: string }
  | { kind: 'none' };

interface Rule {
  actions: readonly Action[];
  scope: ScopeKind;
  /** Why, in the words shown to a denied caller and printed in the matrix. */
  note?: string;
}

const NOTHING: Rule = { actions: [], scope: 'none' };

const ALL_ACTIONS = ACTIONS;

/** Owner runs the business and sees everything. */
const OWNER_RULE: Rule = { actions: ALL_ACTIONS, scope: 'all' };

const ownerTable: Record<ResourceKind, Rule> = {
  client: OWNER_RULE,
  application: OWNER_RULE,
  project: OWNER_RULE,
  feature: OWNER_RULE,
  task: OWNER_RULE,
  user: OWNER_RULE,
  timeEntry: OWNER_RULE,
  payRate: OWNER_RULE,
  billingRate: OWNER_RULE,
  invoice: OWNER_RULE,
  benefit: OWNER_RULE,
  spend: OWNER_RULE,
  capacity: OWNER_RULE,
};

/**
 * Contractor: sees all BotGuide work, owns their own hours and their own rate,
 * and is blind to everything the client is charged.
 *
 * ## 2026-09-22: the work tree is shared
 *
 * A contractor now has the same hand as the owner over client, application,
 * project, feature and task - create, update and delete. ADR-0005 reserved
 * creating and deleting for the owner on the reasoning that Ryan builds the
 * month and sizes it; Ryan overruled that, which is what the ADR was written
 * to allow. The amendment at its foot is where the decision lives.
 *
 * ## What did NOT move, and why each one matters
 *
 * The two guarantees at the top of this file are untouched, and widening the
 * tree is precisely when they are easiest to lose:
 *
 *   - `payRate` is still scope `self`. A contractor sees their own rate and
 *     nobody else's - that is guarantee 1, and the seed now has two
 *     contractors so it is falsifiable rather than theoretical.
 *   - `billingRate`, `invoice`, `spend` and `benefit` are still denied
 *     outright. What a client is charged is not a contractor's concern, and
 *     being able to create a client does not change that.
 *   - `timeEntry` is still scope `self`: own hours only.
 *   - `approve` is still owner-only on every resource, so a contractor cannot
 *     approve their own hours. This is the one that a blanket "same as the
 *     owner" would have quietly destroyed.
 *   - `user` is still list and read. People are not work: the roster is what
 *     the pay boundary hangs off, so creating and deactivating a person stays
 *     with the owner.
 */
const TREE_WRITE = ['list', 'read', 'create', 'update', 'delete'] as const;

const contractorTable: Record<ResourceKind, Rule> = {
  client: { actions: [...TREE_WRITE], scope: 'all', note: 'The work tree is shared with the owner' },
  application: { actions: [...TREE_WRITE], scope: 'all' },
  project: { actions: [...TREE_WRITE], scope: 'all' },
  feature: { actions: [...TREE_WRITE], scope: 'all' },
  task: {
    actions: [...TREE_WRITE, 'move', 'assign'],
    scope: 'all',
    note: 'Same hand as the owner: builds the work, moves it, assigns it',
  },
  user: {
    actions: ['list', 'read'],
    scope: 'all',
    note: 'Roster and capacity only - pay fields are stripped by redactUser',
  },
  timeEntry: {
    actions: ['list', 'read', 'create', 'update', 'delete'],
    scope: 'self',
    note: 'Own hours only, and only while pending',
  },
  payRate: { actions: ['list', 'read'], scope: 'self', note: 'Own rate only; they negotiated it' },
  billingRate: { ...NOTHING, note: 'What the client is charged is not a contractor concern' },
  invoice: { ...NOTHING, note: 'Client billing is owner-only' },
  benefit: { ...NOTHING, note: 'Client ROI inputs are owner-only' },
  spend: { ...NOTHING, note: 'Client spend is owner-only' },
  capacity: { actions: ['list', 'read'], scope: 'all', note: 'Points, not money - the team plans together' },
};

/**
 * Client: one client's work status, spend and ROI. No people, ever.
 *
 * `user` is denied outright rather than redacted. The requirements table gives
 * the client work lanes, spend and ROI and nothing about individuals, and a
 * redacted roster is still a roster - it leaks who we employ and how many.
 */
const clientTable: Record<ResourceKind, Rule> = {
  client: { actions: ['read'], scope: 'client', note: 'Their own record only' },
  application: { actions: ['list', 'read'], scope: 'client' },
  project: { actions: ['list', 'read'], scope: 'client' },
  feature: { actions: ['list', 'read'], scope: 'client' },
  task: { actions: ['list', 'read'], scope: 'client', note: 'Status only; assignee is stripped by redactTask' },
  user: { ...NOTHING, note: 'A redacted roster is still a roster' },
  timeEntry: { ...NOTHING, note: 'Contractor hours are never client-visible' },
  payRate: { ...NOTHING, note: 'Contractor pay is never client-visible' },
  billingRate: { ...NOTHING, note: 'Rates are internal; the client sees totals, not their derivation' },
  invoice: { ...NOTHING, note: 'Tier B - spend is exposed instead, already allocated' },
  benefit: { actions: ['list', 'read'], scope: 'client', note: 'Their own projected ROI inputs' },
  spend: { actions: ['list', 'read'], scope: 'client', note: 'Spend per application, their own' },
  capacity: { ...NOTHING, note: 'Internal resourcing' },
};

export const RULES: Record<Role, Record<ResourceKind, Rule>> = {
  owner: ownerTable,
  contractor: contractorTable,
  client: clientTable,
};

/* -------------------------------------------------------------------------- */
/* Decisions                                                                  */
/* -------------------------------------------------------------------------- */

export type Decision = { allow: true; scope: Scope } | { allow: false; reason: string };

/**
 * Resolve a rule's scope kind against a concrete actor.
 *
 * A `client`-scoped rule held by an actor with no clientId collapses to `none`.
 * That is not defensive noise: it is the case where a misconfigured client user
 * would otherwise see every client, and it must fail closed.
 */
function resolveScope(actor: Actor, kind: ScopeKind): Scope {
  switch (kind) {
    case 'all':
      return { kind: 'all' };
    case 'self':
      return { kind: 'self', userId: actor.id };
    case 'client':
      return actor.clientId ? { kind: 'client', clientId: actor.clientId } : { kind: 'none' };
    case 'none':
      return { kind: 'none' };
  }
}

/**
 * The single entry point. Returns the row scope on success and a printable
 * reason on failure, so a 403 can say something true without leaking shape.
 */
export function authorize(actor: Actor | null, action: Action, kind: ResourceKind): Decision {
  if (!actor) return { allow: false, reason: 'Not signed in' };

  const rule = RULES[actor.role][kind];

  if (!rule.actions.includes(action)) {
    return {
      allow: false,
      reason: rule.note ?? `A ${actor.role} may not ${action} ${kind}`,
    };
  }

  const scope = resolveScope(actor, rule.scope);
  if (scope.kind === 'none') {
    return { allow: false, reason: rule.note ?? `A ${actor.role} has no ${kind} in scope` };
  }

  return { allow: true, scope };
}

/** Boolean form, for deciding whether to render a control. */
export function can(actor: Actor | null, action: Action, kind: ResourceKind): boolean {
  return authorize(actor, action, kind).allow;
}

/**
 * Does a concrete row fall inside a scope?
 *
 * The API applies scopes as SQL predicates, so this is the belt to that
 * braces: it is used on single-row fetches and in tests, where a predicate is
 * easy to get subtly wrong and a second, differently-shaped check is cheap.
 */
export function scopeAdmits(
  scope: Scope,
  row: { clientId?: string | null; userId?: string | null },
): boolean {
  switch (scope.kind) {
    case 'all':
      return true;
    case 'none':
      return false;
    case 'client':
      return row.clientId === scope.clientId;
    case 'self':
      return row.userId === scope.userId;
  }
}

/* -------------------------------------------------------------------------- */
/* Field-level rules                                                          */
/* -------------------------------------------------------------------------- */

/**
 * May `actor` see what `subjectUserId` is paid, and what they have earned?
 * The owner, and the person themselves. Nobody else, in any role.
 */
export function canSeeContractorPay(actor: Actor | null, subjectUserId: string): boolean {
  if (!actor) return false;
  if (actor.role === 'owner') return true;
  if (actor.role === 'contractor') return actor.id === subjectUserId;
  return false;
}

/** May `actor` see money the client is charged - rates, invoices, spend, ROI? */
export function canSeeClientMoney(actor: Actor | null, clientId: string): boolean {
  if (!actor) return false;
  if (actor.role === 'owner') return true;
  if (actor.role === 'client') return actor.clientId === clientId;
  return false;
}

/** Strips pay fields from a user record unless the viewer is entitled to them. */
export function redactUser<T extends { id: string; hourlyRateCents?: number | null; email?: string }>(
  actor: Actor | null,
  user: T,
): Omit<T, 'hourlyRateCents'> & { hourlyRateCents?: number | null } {
  if (canSeeContractorPay(actor, user.id)) return user;
  const { hourlyRateCents: _dropped, ...rest } = user;
  return rest;
}

/**
 * Strips internal detail from a task before a client sees it.
 *
 * Two different things, for two different reasons.
 *
 * **The assignee** is person-level data, and a client sees no people at all.
 *
 * **The description** is internal prose, by the same argument as
 * `redactInternalProse` below: it is how we describe the work to ourselves,
 * and unlike a feature there is no `clientOutcome` beside it holding the
 * version written for the client. So there is nothing to keep, and the whole
 * field goes.
 *
 * That was missed until 2026-09-21 because nothing rendered it. The client
 * dashboard does not show task descriptions, so no screen gave it away — but
 * `/api/tasks` is readable by a client and carried the field, and against the
 * real seed 63 descriptions came back, 14 of which named somebody, including
 * BotGuide's own owner. Redacting the dashboard while leaving the collection
 * readable protects nothing, which is the same lesson the assignee *filter*
 * taught: see the note on `taskFilters` in `routes/entities.ts`.
 */
export function redactTask<
  T extends { assigneeId?: string | null; assigneeName?: string | null; description?: string },
>(actor: Actor | null, task: T): T {
  if (!actor || actor.role !== 'client') return task;
  return { ...task, assigneeId: null, assigneeName: null, description: '' };
}

/**
 * Blanks internal prose on a project or feature before a client sees it.
 *
 * `summary` is how we describe the work to ourselves. `clientOutcome` is how we
 * describe it to the client. They are different fields for exactly this reason,
 * and one seeded `summary` already reads "...and the developer handoff to Cody
 * on Jul 8".
 *
 * Until this existed, nothing stopped that reaching Shane. The client dashboard
 * happened not to render `summary`, which is a convention held by one component
 * - and the first person to add a description to a card would have broken it
 * without ever touching anything that looked like a permission.
 *
 * The API cannot sanitise prose, so it does not try. It removes the field a
 * client has no reason to receive, and the leak stops being possible rather
 * than merely being avoided.
 */
export function redactInternalProse<T extends { summary?: string }>(actor: Actor | null, row: T): T {
  if (!actor || actor.role !== 'client') return row;
  return { ...row, summary: '' };
}

/* -------------------------------------------------------------------------- */
/* Introspection - used by tools/policy-matrix.mjs and the test suite          */
/* -------------------------------------------------------------------------- */

export interface MatrixCell {
  role: Role;
  kind: ResourceKind;
  action: Action;
  allowed: boolean;
  scope: ScopeKind;
  note: string | null;
}

/** Every (role x resource x action) triple, as data. */
export function policyMatrix(): MatrixCell[] {
  const cells: MatrixCell[] = [];
  for (const role of Object.keys(RULES) as Role[]) {
    for (const kind of RESOURCE_KINDS) {
      const rule = RULES[role][kind];
      for (const action of ACTIONS) {
        cells.push({
          role,
          kind,
          action,
          allowed: rule.actions.includes(action),
          scope: rule.scope,
          note: rule.note ?? null,
        });
      }
    }
  }
  return cells;
}
